// lib/social-channels.ts
//
// Single source of truth for the org's owned social profiles. Three consumers:
//   1. SiteFooter.tsx        — the footer's outbound social icon links
//   2. lib/schema.jsonld     — the canonical Organization `sameAs` (the one
//      site-wide #org node injected by app/layout.tsx). SOCIAL_PROFILE_URLS is
//      bound to it by social-channels.test.ts so the two can never drift.
//   3. UTM inbound tagging    — each channel's `utmSource` is the canonical
//      utm_source value, so the taxonomy cannot fragment (see lib/utm.ts).
//
// Keeping the list here (rather than duplicated inline) is what caught the
// sameAs drift: an earlier per-page #org copy listed only 6 of the 9 profiles.

import { withUtm } from "@/lib/utm";

export const SITE_URL = "https://eclatinstitute.sg";

export const SOCIAL_CHANNEL_KEYS = [
  "instagram",
  "tiktok",
  "lemon8",
  "threads",
  "youtube",
  "linkedin",
  "facebook",
  "xiaohongshu",
  "x",
] as const;

export type SocialChannelKey = (typeof SOCIAL_CHANNEL_KEYS)[number];

export interface SocialChannel {
  /** Stable key; also the `utm_source` value and the footer `data-ga-label`. */
  key: SocialChannelKey;
  /** Display name, e.g. "TikTok". Used in aria-label ("Eclat Institute on …"). */
  name: string;
  /** Outbound profile URL (footer link + sameAs). */
  profileUrl: string;
  /** Canonical utm_source for inbound links from this channel. */
  utmSource: SocialChannelKey;
  /** Screen-reader subject preceding "on <name>", preserving current a11y text. */
  srSubject: string;
}

// Order mirrors the footer's rendered order.
export const SOCIAL_CHANNELS: readonly SocialChannel[] = [
  {
    key: "instagram",
    name: "Instagram",
    profileUrl: "https://www.instagram.com/eclatinstitute/",
    utmSource: "instagram",
    srSubject: "@eclatinstitute",
  },
  {
    key: "tiktok",
    name: "TikTok",
    profileUrl: "https://www.tiktok.com/@eclat_institute",
    utmSource: "tiktok",
    srSubject: "@eclat_institute",
  },
  {
    key: "lemon8",
    name: "Lemon8",
    profileUrl: "https://www.lemon8-app.com/@eclat_institute",
    utmSource: "lemon8",
    srSubject: "@eclat_institute",
  },
  {
    key: "threads",
    name: "Threads",
    profileUrl: "https://www.threads.com/@eclatinstitute",
    utmSource: "threads",
    srSubject: "@eclat_institute",
  },
  {
    key: "youtube",
    name: "YouTube",
    profileUrl: "https://www.youtube.com/@EclatInstitute",
    utmSource: "youtube",
    srSubject: "@EclatInstitute",
  },
  {
    key: "linkedin",
    name: "LinkedIn",
    profileUrl: "https://www.linkedin.com/company/eclatinstitute/",
    utmSource: "linkedin",
    srSubject: "Eclat Institute",
  },
  {
    key: "facebook",
    name: "Facebook",
    profileUrl: "https://www.facebook.com/eclatinstitute",
    utmSource: "facebook",
    srSubject: "Eclat Institute",
  },
  {
    key: "xiaohongshu",
    name: "Xiaohongshu",
    profileUrl: "https://xhslink.com/m/1ugd3X4RuMw",
    utmSource: "xiaohongshu",
    srSubject: "Eclat Institute",
  },
  {
    key: "x",
    name: "X",
    profileUrl: "https://x.com/eclat_institute",
    utmSource: "x",
    srSubject: "@eclat_institute",
  },
];

/** Profile URLs in registry order — feeds the Organization `sameAs` JSON-LD. */
export const SOCIAL_PROFILE_URLS: readonly string[] = SOCIAL_CHANNELS.map(
  (channel) => channel.profileUrl
);

/**
 * The tagged inbound URL to paste into a channel's bio/profile link field.
 * Same medium + campaign across every channel so `utm_source` is a clean
 * comparison of platform effectiveness for identical cross-posted content.
 */
export function socialBioLink(
  channel: SocialChannel,
  destination: string = SITE_URL
): string {
  return withUtm(destination, {
    source: channel.utmSource,
    medium: "social",
    campaign: "bio",
  });
}
